import { z } from "zod";
import { apiRequest } from "@/lib/http";

export const userSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string().optional(),
  tenantId: z.string().optional(),
  role: z.string().optional(),
  permissions: z.union([z.string(), z.array(z.string())]).optional(),
  tenants: z.array(z.string()).optional(),
  mfaEnabled: z.boolean().optional(),
  authRequired: z.boolean().optional(),
});
export type ApiUser = z.infer<typeof userSchema>;

const tokenResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  user: userSchema.optional(),
});
export type TokenResponse = z.infer<typeof tokenResponseSchema>;

const loginResultSchema = z.union([
  z.object({ mfaRequired: z.literal(true), mfaToken: z.string() }),
  tokenResponseSchema,
]);
export type LoginResult = z.infer<typeof loginResultSchema>;

const ssoProviderSchema = z.object({
  name: z.string(),
  displayName: z.string().optional(),
});
export type SsoProvider = z.infer<typeof ssoProviderSchema>;

const setupStatusSchema = z.object({
  needsSetup: z.boolean(),
  authConfigured: z.boolean().optional(),
  authRequired: z.boolean().optional(),
});
export type SetupStatus = z.infer<typeof setupStatusSchema>;

export const authApi = {
  me: () => apiRequest<unknown>("/auth/me").then((r) => userSchema.parse(r)),

  setupStatus: () =>
    apiRequest<unknown>("/auth/setup-status", { anonymous: true })
      .then((r) => setupStatusSchema.parse(r))
      .catch(() => ({ needsSetup: false }) as SetupStatus),

  setup: (email: string, password: string, displayName: string) =>
    apiRequest<unknown>("/auth/setup", {
      method: "POST",
      anonymous: true,
      body: { email, password, displayName },
    }).then((r) => tokenResponseSchema.parse(r)),

  login: (email: string, password: string) =>
    apiRequest<unknown>("/auth/login", {
      method: "POST",
      anonymous: true,
      body: { email, password },
    }).then((r) => loginResultSchema.parse(r)),

  mfaComplete: (mfaToken: string, code?: string, backupCode?: string) =>
    apiRequest<unknown>("/auth/mfa/complete", {
      method: "POST",
      anonymous: true,
      body: { mfaToken, code, backupCode },
    }).then((r) => tokenResponseSchema.parse(r)),

  logout: () => apiRequest<void>("/auth/logout", { method: "POST" }),

  oidcProviders: () =>
    apiRequest<unknown>("/auth/oidc/providers", { anonymous: true })
      .then((r) => z.array(ssoProviderSchema).parse(r))
      .catch(() => [] as SsoProvider[]),

  samlProviders: () =>
    apiRequest<unknown>("/auth/saml/providers", { anonymous: true })
      .then((r) => z.array(ssoProviderSchema).parse(r))
      .catch(() => [] as SsoProvider[]),
};
